/**
 * Time-aware greeting helper.
 *
 * Per plan §12 + Doc 04:
 *  - 05:00-10:59  → "Selamat pagi"
 *  - 11:00-14:59  → "Selamat siang"
 *  - 15:00-17:59  → "Sore ini"
 *  - 18:00-04:59  → "Malam"
 *
 * Boundaries are inclusive at the start hour. Pure function (no
 * side effects) so it's trivially testable. Production callers
 * should pass `new Date()` — tests pass a fixed `Date` for stable
 * assertions.
 *
 * Phase 3 task #11.
 */

export type TimeOfDay = 'morning' | 'afternoon' | 'evening' | 'night';

const HOUR_MORNING_START = 5;
const HOUR_AFTERNOON_START = 11;
const HOUR_EVENING_START = 15;
const HOUR_NIGHT_START = 18;

export function getTimeOfDay(date: Date): TimeOfDay {
  const hour = date.getHours();
  if (hour >= HOUR_NIGHT_START || hour < HOUR_MORNING_START) return 'night';
  if (hour >= HOUR_EVENING_START) return 'evening';
  if (hour >= HOUR_AFTERNOON_START) return 'afternoon';
  return 'morning';
}

export type Greeting = {
  /** "Selamat pagi!" — full copy. */
  text: string;
  /** Bucket for tests / analytics + pemilihan ikon greeting (lib/icons). */
  timeOfDay: TimeOfDay;
};

const GREETINGS: Record<TimeOfDay, string> = {
  morning: 'Selamat pagi!',
  afternoon: 'Selamat siang!',
  evening: 'Sore ini!',
  night: 'Malam!',
};

/**
 * Resolve a greeting for a given date. Defaults to `new Date()` so
 * screen code stays one-liner: `greeting()`.
 *
 * Fase 3 redesign: emoji greeting (🌞☀️🌅🌙) dihapus — ikon matahari/
 * bulan clay dirender via greetingIcon(timeOfDay) di komponen.
 */
export function greeting(date: Date = new Date()): Greeting {
  const timeOfDay = getTimeOfDay(date);
  return { text: GREETINGS[timeOfDay], timeOfDay };
}
